import { useEffect, useRef } from 'react'

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

/** Keep keyboard focus inside a dialog and return it to the launcher on close. */
export function useDialogFocus (open = true) {
  const dialogRef = useRef(null)
  const returnFocusRef = useRef(null)
  const wasOpenRef = useRef(false)

  // Capture the launcher during render, before React commits any autoFocus
  // inside the dialog. Capturing in useEffect is too late for such dialogs.
  if (open && !wasOpenRef.current && typeof document !== 'undefined') {
    returnFocusRef.current = document.activeElement
  }
  wasOpenRef.current = open

  useEffect(() => {
    if (!open || typeof document === 'undefined') return undefined
    const dialog = dialogRef.current
    if (!dialog) return undefined

    const previousFocus = returnFocusRef.current || document.activeElement
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const focusable = () => [...dialog.querySelectorAll(FOCUSABLE)]
      .filter(element => !element.hidden && element.getAttribute('aria-hidden') !== 'true')

    const frame = window.requestAnimationFrame(() => {
      // React's autoFocus runs during commit. Preserve that deliberate target
      // instead of replacing it with the first DOM button (often “Schließen”).
      if (dialog.contains(document.activeElement) && document.activeElement !== dialog) return
      const target = dialog.querySelector('[data-autofocus="true"], [autofocus]') || focusable()[0] || dialog
      target.focus?.({ preventScroll: true })
    })
    const trapFocus = event => {
      if (event.key !== 'Tab') return
      const items = focusable()
      if (!items.length) {
        event.preventDefault()
        dialog.focus?.()
        return
      }
      const first = items[0]
      const last = items[items.length - 1]
      if (event.shiftKey && (document.activeElement === first || !dialog.contains(document.activeElement))) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && (document.activeElement === last || !dialog.contains(document.activeElement))) {
        event.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', trapFocus)
    return () => {
      window.cancelAnimationFrame(frame)
      document.removeEventListener('keydown', trapFocus)
      document.body.style.overflow = previousOverflow
      if (previousFocus?.isConnected) {
        window.requestAnimationFrame(() => previousFocus.focus?.({ preventScroll: true }))
      }
      returnFocusRef.current = null
    }
  }, [open])

  return dialogRef
}

export default useDialogFocus
